import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, message, Tooltip, Popconfirm, Tag, Row, Col, Modal } from 'antd';
import {
    EditOutlined, DeleteOutlined, MobileOutlined, EyeOutlined, EyeInvisibleOutlined, PlusOutlined, DesktopOutlined,
    SlackOutlined, RocketOutlined, MessageOutlined, CloudServerOutlined, QrcodeOutlined, SkinOutlined, WifiOutlined,
    InboxOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import DeviceConfigEditForm from '../DeviceConfigs/DeviceConfigEditFormContainer';
import { useTheme } from '../../context/ThemeContext';
import DeviceTransferModal from '../DeviceConfigs/DeviceTransferModal';
import DeviceConfigPreview from '../DeviceConfigs/DeviceConfigPreview';
import SendMessagePopup from '../DeviceConfigs/SendMessagePopup';

const DeviceConfigListAdmin = () => {
  const { darkTheme } = useTheme();
  // Hook-based modal so the confirm dialog follows the app theme (dark/light)
  const [modal, modalContextHolder] = Modal.useModal();
  const getUniqueValues = (keyPath) => {
    const values = deviceConfigs.map(item => {
      const keys = keyPath.split('.');
      return keys.reduce((acc, key) => acc?.[key], item);
    }).filter(Boolean);

    return [...new Set(values)].map(value => ({
      text: value,
      value: value,
    }));
  };

  const [deviceConfigs, setDeviceConfigs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDeviceConfig, setEditingDeviceConfig] = useState(null);
  const [showVisible, setShowVisible] = useState(true);
  const [sendMessagePopupRecord, setSendMessagePopupRecord] = useState(null);

  const navigate = useNavigate();  

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const fetchDeviceConfigs = useCallback(async () => {
    try {
      const response = await axios.get(`/deviceConfigs/admin`);
      const filteredDeviceConfigs = response.data.filter(deviceConfig => deviceConfig.IsVisible === showVisible);

      setDeviceConfigs(filteredDeviceConfigs);
    } catch (error) {
      console.error('Error fetching deviceConfigs:', error);
    }
  }, [showVisible]); 

  useEffect(() => {
    fetchDeviceConfigs();
  }, [fetchDeviceConfigs]);

  const handleDeleteAdmin = async (record) => {
    try {
      const updatedRecord = { ...record, isDeleted: !record.isDeleted };
      await axios.put(`/deviceConfigs/admin/${record._id}`, updatedRecord);
      setDeviceConfigs(prevDeviceConfigs =>
        prevDeviceConfigs.map(deviceConfig =>
          deviceConfig._id === record._id ? updatedRecord : deviceConfig
        )
      );
      message.success(`DeviceConfig ${updatedRecord.DeviceConfigName} has been ${record.isDeleted ? 'restored' : 'archived'} successfully.`);
    } catch (error) {
      message.error('Error updating archived state. Please try again.');
    }
  };

  // Asks for confirmation before removing a device config from the database for good
  const confirmHardDelete = (record) => {
    modal.confirm({
      title: 'Delete device config permanently?',
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      content: `The device config "${record.DeviceConfigName}" will be permanently removed from the database. This cannot be undone.`,
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      centered: true,
      styles: { content: { backgroundColor: darkTheme ? '#2a1215' : '#fff1f0' } },
      onOk: () => handleHardDelete(record),
    });
  };

  const handleHardDelete = async (record) => {
    try {
      await axios.delete(`/deviceConfigs/admin/${record._id}`);
      setDeviceConfigs(prevDeviceConfigs =>
        prevDeviceConfigs.filter(deviceConfig => deviceConfig._id !== record._id)
      );
      message.success(`DeviceConfig ${record.DeviceConfigName} has been permanently deleted.`);
    } catch (error) {
      message.error('Error deleting device config permanently. Please try again.');
    }
  };

  const handleEdit = (record) => {
    setEditingDeviceConfig(record);
    setIsModalOpen(true); 
  };

  const handleAdd = () => {
    setEditingDeviceConfig(null); 
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false); 
    setEditingDeviceConfig(null); 
  };

  const handlePreviewModalCancel = () => {
    setIsPreviewModalOpen(false);
    setEditingDeviceConfig(null);
  };
  
  const handleToggleVisibility = async (record) => {
    try {
      const updatedRecord = { ...record, IsVisible: !record.IsVisible };
      await axios.put(`/deviceConfigs/${record._id}`, updatedRecord);
      setDeviceConfigs(prevDeviceConfigs =>
        prevDeviceConfigs.map(deviceConfig =>
          deviceConfig._id === record._id ? updatedRecord : deviceConfig
        )
      );
      message.success(`DeviceConfig ${updatedRecord.DeviceConfigName} visibility updated.`);
    } catch (error) {
      message.error('Error updating visibility. Please try again.');
    }
  };

  const handleToggleVisibilityFilter = () => {
    setShowVisible(!showVisible);
  };

  const handleDevices = (record) => {
    setEditingDeviceConfig(record);
    setIsTransferModalOpen(true);
  };

  const handlePreview = (record) => {
    setEditingDeviceConfig(record);
    setIsPreviewModalOpen(true); 
  };

  const handleSlack = (record) => {
    navigate(`/slack-notifications/${record._id}`);
  };

  const handleTransferSuccess = () => {
    setIsTransferModalOpen(false);
    fetchDeviceConfigs();
  };

  const handleUpdateConfig = async (record) => {
    try {
      const predefinedMessage = "This is a test message for online devices.";
      let DeviceConfigId= record._id;
      // Call to the backend to send the message
      await axios.post('/ws/update-config', {
        DeviceConfigId,
        message: predefinedMessage,
      });

      message.success(`Messages sent to online devices for Configuration: ${record.DeviceConfigName}`);
    } catch (error) {
      message.error(`Error sending message: ${record.DeviceConfigName}`);
    }
  };

    // Push ServerOverrideUrl to devices sending only DeviceConfigId.
    // Backend will read the config from DB, resolve ServerOverrideUrl and broadcast via WS.
    const handlePushServerOverride = async (record) => {
        try {
            const DeviceConfigId = record._id;

            await axios.post('/ws/push-server-override', {
                DeviceConfigId,
            });

            message.success(`Server override pushed to online devices for: ${record.DeviceConfigName}`);
        } catch (e) {
            console.error(e);
            const backendMsg = e?.response?.data?.message;
            message.error(backendMsg || `Failed to push server override for: ${record.DeviceConfigName}`);
        }
    };

  const handleSendMessage = (record) => {
    setSendMessagePopupRecord(record);
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'Type',
      key: 'Type',
        render: (type, record) => {
            const baseStyle = { fontSize: 18 };
            console.log('record.mode:', record?.Mode);
            const typeIcon =
                type === 'mobile' ? <MobileOutlined style={{ ...baseStyle, color: '#52c41a' }} /> :
                    type === 'desktop' ? <DesktopOutlined style={{ ...baseStyle, color: '#0078D6' }} /> :
                        type === 'leadcapture' ? <QrcodeOutlined style={{ ...baseStyle, color: '#722ED1' }} /> :
                            type === 'entranceControl' ? <QrcodeOutlined style={{ ...baseStyle, color: '#722ED1' }} /> :
                                null;

            const entranceControlModeIcon =
                type === 'mobile' && record?.Mode === 'cloakroom'
                    ? <SkinOutlined style={{ ...baseStyle, marginLeft: 8 }} />
                    : type === 'mobile' && record?.Mode === 'nfcLink'
                        ? <WifiOutlined style={{ ...baseStyle, marginLeft: 8 }} />
                        : null;

            return (
                <Space size={6}>
                    {typeIcon}
                    {entranceControlModeIcon}
                </Space>
            );
        },
    },
    {
      title: 'Mnemonic',
      dataIndex: ['SelectedEventId', 'mnemonic'], 
      key: 'mnemonic',
      filters: getUniqueValues('SelectedEventId.mnemonic'),
      onFilter: (value, record) =>
          record.SelectedEventId?.mnemonic?.toLowerCase().includes(value.toLowerCase()),
      filterSearch: true,
      sorter: (a, b) => {
        const nameA = a.SelectedEventId?.mnemonic?.toLowerCase() || '';
        const nameB = b.SelectedEventId?.mnemonic?.toLowerCase() || '';
        return nameA.localeCompare(nameB); 
      },
      render: (_, record) => {
        const mnemonic = record.SelectedEventId?.mnemonic || '';
        const tagColor = record.clientID?.clientColor || 'blue';
    
        return mnemonic ? (
          <Tag color={tagColor}>{mnemonic}</Tag>
        ) : (
          <Tag color="red">N/A</Tag>
        );
      },
    },
    {
      title: 'Device Config Name',
      dataIndex: 'DeviceConfigName',
      key: 'DeviceConfigName',
      sorter: (a, b) => {
        const nameA = a.DeviceConfigName?.toLowerCase() || '';
        const nameB = b.DeviceConfigName?.toLowerCase() || '';
        return nameA.localeCompare(nameB); 
      },
    },
    {
      title: 'Client',
      dataIndex: ['clientID', 'name'],
      key: 'clientID',
      responsive: ['md', 'lg'],
      filters: getUniqueValues('clientID.name'),
      onFilter: (value, record) =>
          record.clientID?.name?.toLowerCase().includes(value.toLowerCase()),
      filterSearch: true,
      render: (text, record) => (
          record.clientID?.name ? (
              <Tag color={record.clientID?.clientColor || 'default'}>
                {record.clientID.name}
              </Tag>
          ) : (
              <Tag color="red">N/A</Tag>
          )
      ),
      sorter: (a, b) => {
        const nameA = a.clientID?.name?.toLowerCase() || '';
        const nameB = b.clientID?.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          
          <Tooltip title="Devices" color="blue">
            <Button icon={<MobileOutlined style={{ color: 'skyblue' }}/>} onClick={() => handleDevices(record)} />
          </Tooltip>
          <Tooltip title={record.Type === 'desktop' ? "Preview" : ""} color="orange">
            <Button
                icon={<DesktopOutlined style={{ color: 'orange' }} />}
                onClick={() => record.Type === 'desktop' && handlePreview(record)}
                disabled={record.Type !== 'desktop'}
                style={{
                  visibility: record.Type === 'desktop' ? 'visible' : 'hidden',
                }}
            />
          </Tooltip>
          <Tooltip title="Slack" color="#611f69">
            <Button 
              icon={<SlackOutlined style={{ color: '#611f69' }}/>} 
              onClick={() => handleSlack(record)} 
              style={{ marginLeft: '20px' }} 
            />
          </Tooltip>
          <Tooltip title="Update devices Configuration" color="orangered">
            <Button 
              icon={<RocketOutlined style={{ color: 'orangered' }}/>} 
              onClick={() => handleUpdateConfig(record)}
            />
          </Tooltip>
          <Tooltip
            title={record.ServerOverrideUrl ? "Push Server URL to devices" : "Set Server URL in the 'Server' tab"}
            color={record.ServerOverrideUrl ? "royalblue" : "gray"}
          >
            <Button
                icon={<CloudServerOutlined style={{ color: record.ServerOverrideUrl ? 'royalblue' : 'gray' }} />}
                onClick={() => handlePushServerOverride(record)}
                disabled={!record.ServerOverrideUrl}
            />
          </Tooltip>
          <Tooltip title="Send message" color="green">
            <Button
              icon={<MessageOutlined style={{ color: 'green' }} />}
              onClick={() => handleSendMessage(record)}
            />
          </Tooltip>

          <Tooltip title="Edit" color="blue">
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ marginLeft: '20px' }} />
          </Tooltip>
          <Popconfirm
            title={record.isDeleted ? "Are you sure you want to restore this device config?" : "Are you sure you want to archive this device config?"}
            onConfirm={() => handleDeleteAdmin(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title={record.isDeleted ? "Restore" : "Archive"} color="orange">
              <Button icon={record.isDeleted ? <InboxOutlined style={{ color: 'red' }}  /> : <InboxOutlined />} />
            </Tooltip>
          </Popconfirm>
          <Tooltip title="Delete permanently" color="red">
            <Button danger icon={<DeleteOutlined />} onClick={() => confirmHardDelete(record)} />
          </Tooltip>
          <Tooltip title={record.IsVisible ? "Visible" : "Invisible"} color={record.IsVisible ? "green" : "gray"}>
            <Button
              icon={record.IsVisible ? <EyeOutlined /> : <EyeInvisibleOutlined style={{ color: 'red' }} />}
              onClick={() => handleToggleVisibility(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px'}}>
      {modalContextHolder}
      <h1 style={{ marginBottom: '24px' }}>Device Configs List</h1>
      <Row Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={12} md={6} lg={4}>
          <Button
            icon={showVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={handleToggleVisibilityFilter}
            style={{ width: '100%' }}
          >
            {showVisible ? 'Show Hidden' : 'Show Visible'}
          </Button>
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={handleAdd}
            style={{ width: '100%' }}
          >
            <span className="hide-text-mobile">Add</span>
          </Button>
        </Col>
      </Row>
      
      <Table
        columns={columns}
        dataSource={deviceConfigs}
        rowKey="_id"
        scroll={{ x: 'max-content' }}
      />

      {/* Modal for Editing and Adding DeviceConfig */}
      <DeviceConfigEditForm
        admin
        open={isModalOpen}
        initialValues={editingDeviceConfig || {}} 
        onCancel={handleModalCancel}
        onFinish={fetchDeviceConfigs}
      />

      {/* Other Modals */}
      <DeviceTransferModal
        open={isTransferModalOpen}
        onCancel={() => setIsTransferModalOpen(false)}
        deviceConfig={editingDeviceConfig}
        onTransferSuccess={handleTransferSuccess}
      />

      <DeviceConfigPreview
        open={isPreviewModalOpen}
        onCancel={handlePreviewModalCancel}
        deviceConfig={editingDeviceConfig} 
      />

      <SendMessagePopup
          open={!!sendMessagePopupRecord}
          record={sendMessagePopupRecord}
          onClose={() => setSendMessagePopupRecord(null)}
      />

    </div>
  );
};

export default DeviceConfigListAdmin;
